import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { statusLabel } from "@/components/StatusTracker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  DollarSign, TrendingUp, ImageIcon, Palette, Clock, CheckCircle2, BarChart3, Loader2,
  Sparkles, LogOut, UploadCloud, Trash2, Mail, RotateCcw, Wand2, Zap, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import FaithAdmin from "@/pages/FaithAdmin";
import { track, trackRefund } from "@/lib/analytics";

const STATUSES = ["order_received", "photos_received", "in_design", "quality_review", "ready", "delivered"];
const FILTER_STATUSES = [...STATUSES, "fulfillment_error", "partially_refunded", "refunded"];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-cream">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-amber flex items-center justify-center text-white"><Sparkles className="w-4 h-4" /></span>
            <span className="font-display font-bold text-slateink">Studio Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="overview">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="production" data-testid="admin-tab-production">Quality Review</TabsTrigger>
            <TabsTrigger value="faith" data-testid="admin-tab-faith">Faith Collection</TabsTrigger>
            <TabsTrigger value="marketing" data-testid="admin-tab-marketing">Marketing</TabsTrigger>
            <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="admin-tab-discounts">Discounts</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="overview"><Overview /></TabsContent>
          <TabsContent value="orders"><Orders /></TabsContent>
          <TabsContent value="production"><Production /></TabsContent>
          <TabsContent value="faith"><FaithAdmin /></TabsContent>
          <TabsContent value="marketing"><Marketing /></TabsContent>
          <TabsContent value="products"><Products /></TabsContent>
          <TabsContent value="discounts"><Discounts /></TabsContent>
          <TabsContent value="reviews"><Reviews /></TabsContent>
          <TabsContent value="leads"><Leads /></TabsContent>
          <TabsContent value="settings"><Settings /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      <p className="text-2xl font-display font-bold text-slateink mt-3">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div>;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
        <Stat icon={DollarSign} label="Today's Sales" value={`$${s.todays_sales}`} color="bg-amber-100 text-amber" />
        <Stat icon={TrendingUp} label="Monthly Revenue" value={`$${s.monthly_revenue}`} color="bg-sage/10 text-sage" />
        <Stat icon={ImageIcon} label="Awaiting Photos" value={s.orders_awaiting_photos} color="bg-purple-100 text-purple-600" />
        <Stat icon={Palette} label="In Design" value={s.orders_in_design} color="bg-amber-100 text-amber" />
        <Stat icon={Clock} label="Due Today" value={s.orders_due_today} color="bg-rose/10 text-rose" />
        <Stat icon={CheckCircle2} label="Completed" value={s.completed_orders} color="bg-sage/10 text-sage" />
        <Stat icon={BarChart3} label="Avg Order Value" value={`$${s.average_order_value}`} color="bg-blue-100 text-blue-600" />
        <Stat icon={DollarSign} label="Paid Orders" value={s.total_paid_orders} color="bg-slate-100 text-slate-600" />
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 p-6 mt-6">
        <h3 className="font-semibold text-slateink mb-4">Top-selling products</h3>
        {s.top_products.length === 0 ? <p className="text-slate-400 text-sm">No sales yet.</p> : (
          <div className="space-y-2">
            {s.top_products.map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-slate-700">{p.name}</span>
                <Badge className="bg-amber-100 text-amber-700 border-0">{p.count} sold</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState(null);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("");
  const load = () => api.get("/admin/orders", { params: filter ? { status: filter } : {} }).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, [filter]);
  return (
    <div>
      <div className="flex gap-2 flex-wrap mb-4">
        <button onClick={() => setFilter("")} className={`text-sm px-3 py-1.5 rounded-full ${!filter ? "bg-amber text-white" : "bg-white border border-slate-200"}`}>All</button>
        {FILTER_STATUSES.map((st) => <button key={st} onClick={() => setFilter(st)} className={`text-sm px-3 py-1.5 rounded-full ${filter === st ? "bg-amber text-white" : "bg-white border border-slate-200"}`}>{statusLabel(st)}</button>)}
      </div>
      {orders === null ? <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div> :
        orders.length === 0 ? <p className="text-slate-400 py-10 text-center">No orders.</p> : (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            {orders.map((o) => (
              <button key={o.id} onClick={() => setActive(o)} data-testid={`admin-order-${o.order_number}`} className="w-full flex items-center gap-4 p-4 border-b border-slate-50 hover:bg-subtle text-left">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-slateink">{o.order_number}</span><Badge className="bg-slate-100 text-slate-600 border-0 text-xs">{statusLabel(o.status)}</Badge>{!o.paid && <Badge className="bg-rose/10 text-rose border-0 text-xs">Unpaid</Badge>}{(o.payment_status === "refunded" || o.refunded) && <Badge className="bg-rose text-white border-0 text-xs">Refunded</Badge>}{o.payment_status === "partially_refunded" && <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Partial refund</Badge>}{o.status === "fulfillment_error" && <Badge className="bg-rose text-white border-0 text-xs">Fulfillment error</Badge>}</div>
                  <p className="text-sm text-slate-500 truncate">{o.customer_name} · {o.items.map((i) => i.product_name).join(", ")}</p>
                </div>
                <span className="font-medium text-slateink">${o.total.toFixed(2)}</span>
              </button>
            ))}
          </div>
        )}
      {active && <AdminOrderDialog order={active} onClose={() => setActive(null)} reload={load} />}
    </div>
  );
}

function AdminOrderDialog({ order, onClose, reload }) {
  const [detail, setDetail] = useState(order);
  const [status, setStatus] = useState(order.status === "pending_payment" ? "order_received" : order.status);
  const [notes, setNotes] = useState(order.internal_notes || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => { api.get(`/admin/orders/${order.id}`).then((r) => { setDetail(r.data); setNotes(r.data.internal_notes || ""); }); }, [order.id]);

  const saveStatus = async () => {
    setBusy(true);
    try { await api.patch(`/admin/orders/${order.id}/status`, { status }); toast.success("Status updated & email sent"); reload(); refresh(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const refresh = () => api.get(`/admin/orders/${order.id}`).then((r) => setDetail(r.data));
  const saveNotes = async () => { await api.post(`/admin/orders/${order.id}/notes`, { notes }); toast.success("Notes saved"); };
  const uploadFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); await api.post(`/admin/orders/${order.id}/complete-file`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Finished file uploaded"); refresh(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const refund = async () => { if (!window.confirm("Issue a full refund?")) return; try { await api.post(`/admin/orders/${order.id}/refund`); trackRefund({ transaction_id: detail.order_number, value: detail.total }); toast.success("Refunded"); reload(); refresh(); } catch (err) { toast.error(apiError(err)); } };
  const remind = async (template) => { try { await api.post(`/admin/orders/${order.id}/remind`, { template }); toast.success("Email sent"); } catch (err) { toast.error(apiError(err)); } };

  const pz = detail.personalization || {};
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-order-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{detail.order_number}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-subtle rounded-xl p-4 text-sm">
              <p className="font-semibold text-slateink mb-2">{detail.customer_name} · {detail.customer_email}</p>
              {detail.items.map((i, x) => <p key={x} className="text-slate-600">{i.product_name} — {i.variant.label} {i.upsells?.length ? `(+${i.upsells.length} extras)` : ""}</p>)}
              <p className="text-slate-500 mt-2">Purchased: ${detail.total.toFixed(2)} · Due {(detail.due_date || "").slice(0, 10)}</p>
              {detail.refunded && <Badge className="bg-rose/10 text-rose border-0 mt-2">Refunded</Badge>}
            </div>
            <div className="text-sm space-y-1">
              <p className="font-semibold text-slateink">Personalization</p>
              {Object.entries(pz).filter(([, v]) => v).map(([k, v]) => <p key={k} className="text-slate-600"><span className="text-slate-400">{k.replace(/_/g, " ")}:</span> {v}</p>)}
            </div>
            {detail.assets?.length > 0 && (
              <div>
                <p className="font-semibold text-slateink text-sm mb-2">Uploaded photos ({detail.assets.length})</p>
                <div className="grid grid-cols-4 gap-2">
                  {detail.assets.map((a) => <img key={a.id} src={`${API}/admin/orders/${order.id}/assets/${a.id}`} alt="" className="w-full aspect-square object-cover rounded-lg bg-subtle" />)}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-sm">Order status</Label>
              <div className="flex gap-2 mt-1">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="admin-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={saveStatus} disabled={busy} data-testid="admin-save-status" className="bg-amber hover:bg-amber-600 text-white">Save</Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Ready/Delivered requires an uploaded file. Delivered emails the customer.</p>
            </div>

            <div>
              <Label className="text-sm">Finished file (PDF)</Label>
              <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-slate-200 rounded-xl p-4 text-center mt-1 hover:border-amber">
                <UploadCloud className="w-5 h-5 text-amber mx-auto" /><p className="text-xs text-slate-500 mt-1">{detail.completed_file ? `✓ ${detail.completed_file.filename}` : "Upload finished PDF"}</p>
              </button>
              <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={uploadFile} data-testid="admin-upload-file" />
            </div>

            <div>
              <Label className="text-sm">Internal notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-notes" className="mt-1" />
              <Button variant="outline" size="sm" onClick={saveNotes} className="mt-2">Save notes</Button>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={() => remind("missing_photos")}><Mail className="w-3.5 h-3.5 mr-1" /> Photos reminder</Button>
              <Button variant="outline" size="sm" onClick={() => remind("review")}><Mail className="w-3.5 h-3.5 mr-1" /> Review request</Button>
              {detail.paid && !detail.refunded && <Button variant="outline" size="sm" onClick={refund} className="text-rose border-rose/30"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Refund</Button>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const REGEN_REASONS = [
  "Face does not resemble reference", "Hands incorrect", "Extra person", "Missing person",
  "Wrong hairstyle", "Wrong clothing", "Image too detailed", "Image too simple",
  "Poor composition", "Character inconsistency", "Theme incorrect", "Other",
];

function Production() {
  const [queue, setQueue] = useState(null);
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState(null);
  const load = () => { api.get("/admin/production/queue").then((r) => setQueue(r.data)); api.get("/admin/production-stats").then((r) => setStats(r.data)); };
  useEffect(() => { load(); }, []);
  return (
    <div>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="production-stats">
          <Stat icon={DollarSign} label="AI Spend Today" value={`$${stats.ai_spend_today}`} color="bg-amber-100 text-amber" />
          <Stat icon={TrendingUp} label="AI Spend Month" value={`$${stats.ai_spend_month}`} color="bg-sage/10 text-sage" />
          <Stat icon={BarChart3} label="Avg Cost / Order" value={`$${stats.avg_ai_cost_per_order}`} color="bg-blue-100 text-blue-600" />
          <Stat icon={RotateCcw} label="Avg Regens" value={stats.avg_regenerations_per_order} color="bg-purple-100 text-purple-600" />
          <Stat icon={ShieldAlert} label="AI Failure Rate" value={`${stats.ai_failure_rate}%`} color="bg-rose/10 text-rose" />
        </div>
      )}
      {queue === null ? <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div> :
        queue.length === 0 ? <p className="text-slate-400 py-10 text-center">No books in production. Paid coloring-book orders appear here for review.</p> : (
          <div className="grid gap-3">
            {queue.map((o) => (
              <button key={o.id} onClick={() => setActive(o)} data-testid={`production-order-${o.order_number}`} className="text-left bg-white rounded-2xl border border-slate-100 p-4 card-hover flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${o.production_status === "quality_review" ? "bg-amber-100 text-amber" : o.production_status === "needs_human_review" ? "bg-rose/10 text-rose" : "bg-slate-100 text-slate-500"}`}><Wand2 className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-slateink">{o.order_number}</span>
                    {o.priority === "HIGH" && <Badge className="bg-rose text-white border-0"><Zap className="w-3 h-3 mr-1" />Rush</Badge>}
                    <Badge className="bg-slate-100 text-slate-600 border-0">{o.production_status?.replace(/_/g, " ")}</Badge>
                  </div>
                  <p className="text-sm text-slate-500">{o.customer_name} · {o.page_count} pages · AI ${o.ai_cost?.toFixed?.(3) ?? o.ai_cost}</p>
                </div>
                <span className="text-xs text-slate-400">{(o.due_date || "").slice(0, 10)}</span>
              </button>
            ))}
          </div>
        )}
      {active && <ProductionDialog order={active} onClose={() => setActive(null)} reload={load} />}
    </div>
  );
}

function ProductionDialog({ order, onClose, reload }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reasons, setReasons] = useState({});
  const refresh = () => api.get(`/admin/production/${order.id}`).then((r) => setDetail(r.data.order));
  useEffect(() => { refresh(); }, [order.id]);

  if (!detail) return <Dialog open onOpenChange={onClose}><DialogContent><div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div></DialogContent></Dialog>;
  const prod = detail.production || {};
  const pages = prod.pages || [];

  const regen = async (pageId) => {
    const reason = reasons[pageId] || "Other";
    setBusy(true);
    try { await api.post(`/admin/production/${order.id}/pages/${pageId}/regenerate`, { reason }); toast.success("Page regenerated"); await refresh(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try { await api.post(`/admin/production/${order.id}/approve`); track.bookApproved({ order_category: detail.items?.[0]?.product_slug }); toast.success("Book approved — final PDF rendered & customer emailed"); reload(); onClose(); }
    catch (err) { toast.error(apiError(err)); setBusy(false); }
  };
  const resume = async () => { setBusy(true); track.aiBookGenerationStarted({ order_category: detail.items?.[0]?.product_slug }); try { await api.post(`/admin/production/${order.id}/resume`); toast.success("Generation restarted"); setTimeout(refresh, 1500); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="production-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Quality Review — {detail.order_number}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-3 flex-wrap text-sm text-slate-500 mb-2">
          <span>Theme: <b className="text-slateink">{detail.personalization?.theme || "—"}</b></span>
          <span>Recipient: <b className="text-slateink">{detail.personalization?.recipient_name || "—"}</b></span>
          <span>AI cost: <b className="text-slateink">${prod.ai_cost_total}</b></span>
          <Badge className="bg-slate-100 text-slate-600 border-0">{prod.status?.replace(/_/g, " ")}</Badge>
        </div>
        {prod.quality_checks && !prod.quality_checks.passed && (
          <div className="bg-rose/5 border border-rose/20 rounded-xl p-3 text-sm text-rose">Auto QC flagged: {prod.quality_checks.issues.join(", ")}</div>
        )}
        {prod.status === "needs_human_review" && (
          <Button onClick={resume} disabled={busy} data-testid="production-resume" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold w-fit"><RotateCcw className="w-4 h-4 mr-1" /> Retry generation</Button>
        )}

        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          {pages.map((p) => (
            <div key={p.id} className="border border-slate-100 rounded-2xl overflow-hidden bg-white" data-testid={`production-page-${p.index}`}>
              <div className="grid grid-cols-2">
                <div className="aspect-square bg-subtle"><img src={`${API}/admin/orders/${order.id}/assets/${p.source_asset_id}`} alt="source" className="w-full h-full object-cover" /></div>
                <div className="aspect-square bg-white border-l border-slate-100">
                  {p.image_path ? <img src={`${API}/admin/production/${order.id}/pages/${p.id}/image`} alt="generated" className="w-full h-full object-contain" />
                    : <div className="w-full h-full flex items-center justify-center text-rose text-xs">Failed</div>}
                </div>
              </div>
              <div className="p-3">
                <p className="text-xs text-slate-400 mb-1">Page {p.index + 1} · v{p.version} {p.last_reason ? `· ${p.last_reason}` : ""}</p>
                <div className="flex gap-2">
                  <Select value={reasons[p.id] || ""} onValueChange={(v) => setReasons({ ...reasons, [p.id]: v })}>
                    <SelectTrigger className="h-9 text-xs" data-testid={`regen-reason-${p.index}`}><SelectValue placeholder="Regen reason…" /></SelectTrigger>
                    <SelectContent>{REGEN_REASONS.map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => regen(p.id)} data-testid={`regen-page-${p.index}`}><Wand2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-slate-100">
          <Button variant="outline" asChild><a href={`${API}/admin/production/${order.id}/preview-pdf`} target="_blank" rel="noreferrer">View proof PDF</a></Button>
          <Button onClick={approve} disabled={busy || prod.status === "ready"} data-testid="approve-book" className="rounded-full bg-sage hover:bg-sage-600 text-white font-semibold"><CheckCircle2 className="w-4 h-4 mr-1" /> Approve & deliver</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Products() {
  const [products, setProducts] = useState(null);
  const assetRef = useRef();
  const [uploadFor, setUploadFor] = useState(null);
  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);
  const togglePublish = async (p) => { try { await api.patch(`/admin/products/${p.id}`, { published: !p.published }); load(); } catch (err) { toast.error(apiError(err)); } };
  const changePrice = async (p) => { const v = window.prompt("New starting price ($)", p.starting_price); if (v == null) return; await api.patch(`/admin/products/${p.id}`, { starting_price: parseFloat(v) }); toast.success("Price updated"); load(); };
  const pickAsset = (p) => { setUploadFor(p.id); setTimeout(() => assetRef.current?.click(), 0); };
  const uploadAsset = async (e) => {
    const f = e.target.files?.[0]; if (!f || !uploadFor) return;
    try { const fd = new FormData(); fd.append("file", f); await api.post(`/admin/products/${uploadFor}/download-asset`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Deliverable file uploaded"); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setUploadFor(null); e.target.value = ""; }
  };
  if (!products) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div>;
  return (
    <div className="space-y-6">
      <DigitalProductsDashboard />
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
      <input ref={assetRef} type="file" accept="application/pdf,.zip" className="hidden" onChange={uploadAsset} data-testid="admin-asset-input" />
      {products.map((p) => (
        <div key={p.id} className="flex items-center gap-4 p-4 border-b border-slate-50" data-testid={`admin-product-${p.slug}`}>
          <img src={p.images?.[0]} alt="" className="w-12 h-12 rounded-lg object-cover bg-subtle" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slateink truncate">{p.name}</p>
            <p className="text-sm text-slate-500">{p.category} · from ${p.starting_price}
              {p.is_instant_download && (p.download_asset ? <span className="text-sage"> · file ✓</span> : <span className="text-rose"> · no file</span>)}
            </p>
          </div>
          {p.is_instant_download && <Button variant="outline" size="sm" onClick={() => pickAsset(p)} data-testid={`upload-asset-${p.slug}`}><UploadCloud className="w-3.5 h-3.5 mr-1" />{p.download_asset ? "Replace file" : "Upload file"}</Button>}
          <Button variant="outline" size="sm" onClick={() => changePrice(p)}>Price</Button>
          <div className="flex items-center gap-2"><span className="text-xs text-slate-500">{p.published ? "Live" : "Hidden"}</span><Switch checked={p.published} onCheckedChange={() => togglePublish(p)} data-testid={`toggle-${p.slug}`} /></div>
        </div>
      ))}
      </div>
    </div>
  );
}

const DFILE_COLOR = { none: "bg-red-100 text-red-700", uploaded: "bg-amber-100 text-amber-700", approved: "bg-sage/15 text-sage" };
const DST_COLOR = { file_required: "bg-red-100 text-red-700", draft: "bg-slate-100 text-slate-600", ready_for_review: "bg-amber-100 text-amber-700", approved: "bg-blue-100 text-blue-700", active: "bg-sage/15 text-sage", disabled: "bg-slate-200 text-slate-600" };
const dcap = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function DigitalProductsDashboard() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/products/digital").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="digital-products-dashboard">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slateink text-sm">Digital products</h3>
        <span className="text-xs text-slate-400">{rows.length} products</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
            <th className="px-5 py-2 font-medium">Product</th><th className="px-3 py-2 font-medium">File</th>
            <th className="px-3 py-2 font-medium">Status</th><th className="px-3 py-2 font-medium">Price</th>
            <th className="px-3 py-2 font-medium">Active</th><th className="px-3 py-2 font-medium">Sales</th>
            <th className="px-3 py-2 font-medium">Downloads</th><th className="px-5 py-2 font-medium">Errors</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-50" data-testid={`digital-row-${r.slug}`}>
                <td className="px-5 py-3 font-medium text-slateink">{r.name}</td>
                <td className="px-3 py-3">{r.file_required
                  ? <Badge className="bg-red-100 text-red-700 border-0" data-testid={`file-required-${r.slug}`}>File required</Badge>
                  : <Badge className={`${DFILE_COLOR[r.file_status] || "bg-slate-100 text-slate-600"} border-0`}>{dcap(r.file_status)}</Badge>}</td>
                <td className="px-3 py-3"><Badge className={`${DST_COLOR[r.digital_status] || "bg-slate-100 text-slate-600"} border-0`}>{dcap(r.digital_status)}</Badge></td>
                <td className="px-3 py-3 text-slate-600">${r.price}</td>
                <td className="px-3 py-3">{r.active ? <span className="text-sage font-medium">Active</span> : <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-3 text-slate-600">{r.total_sales}</td>
                <td className="px-3 py-3 text-slate-600">{r.downloads}</td>
                <td className="px-5 py-3">{r.fulfillment_errors > 0 ? <span className="text-red-600 font-semibold">{r.fulfillment_errors}</span> : <span className="text-slate-300">0</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Discounts() {
  const [codes, setCodes] = useState(null);
  const [form, setForm] = useState({ code: "", type: "percent", value: 10 });
  const load = () => api.get("/admin/discounts").then((r) => setCodes(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/admin/discounts", { ...form, value: parseFloat(form.value) }); toast.success("Code created"); setForm({ code: "", type: "percent", value: 10 }); load(); } catch (err) { toast.error(apiError(err)); } };
  const del = async (id) => { await api.delete(`/admin/discounts/${id}`); load(); };
  if (!codes) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div>;
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3 h-fit">
        <h3 className="font-semibold text-slateink">New code</h3>
        <Input placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="discount-code-input" />
        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percent">Percentage %</SelectItem><SelectItem value="fixed">Fixed $</SelectItem></SelectContent></Select>
        <Input type="number" placeholder="Value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="discount-value-input" />
        <Button onClick={create} data-testid="create-discount" className="w-full bg-amber hover:bg-amber-600 text-white">Create</Button>
      </div>
      <div className="md:col-span-2 bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {codes.map((c) => (
          <div key={c.id} className="flex items-center gap-4 p-4 border-b border-slate-50">
            <div className="flex-1"><p className="font-semibold text-slateink">{c.code}</p><p className="text-sm text-slate-500">{c.type === "percent" ? `${c.value}% off` : `$${c.value} off`} · used {c.used_count}×{c.first_purchase_only ? " · first order" : ""}</p></div>
            <Badge className={c.active ? "bg-sage/10 text-sage border-0" : "bg-slate-100 text-slate-500 border-0"}>{c.active ? "Active" : "Off"}</Badge>
            <button onClick={() => del(c.id)} className="text-slate-400 hover:text-rose"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Reviews() {
  const [reviews, setReviews] = useState(null);
  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (r) => { await api.patch(`/admin/reviews/${r.id}`, { approved: !r.approved }); load(); };
  const del = async (r) => { await api.delete(`/admin/reviews/${r.id}`); load(); };
  if (!reviews) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div>;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {reviews.map((r) => (
        <div key={r.id} className="bg-white rounded-2xl border border-slate-100 p-4">
          <div className="flex items-center justify-between"><span className="text-sm font-semibold text-slateink">{r.author} · {r.rating}★</span><Badge className={r.approved ? "bg-sage/10 text-sage border-0" : "bg-slate-100 text-slate-500 border-0"}>{r.approved ? "Shown" : "Hidden"}</Badge></div>
          <p className="text-xs text-slate-400">{r.product_name}</p>
          {r.title && <p className="font-medium text-slateink mt-1">{r.title}</p>}
          <p className="text-sm text-slate-600 mt-1">{r.body}</p>
          <div className="flex gap-2 mt-3"><Button variant="outline" size="sm" onClick={() => toggle(r)}>{r.approved ? "Hide" : "Approve"}</Button><Button variant="outline" size="sm" onClick={() => del(r)} className="text-rose border-rose/30">Delete</Button></div>
        </div>
      ))}
    </div>
  );
}

function Leads() {
  const [leads, setLeads] = useState(null);
  useEffect(() => { api.get("/admin/leads").then((r) => setLeads(r.data)); }, []);
  if (!leads) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div>;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
      {leads.length === 0 ? <p className="text-slate-400 p-8 text-center">No preview leads yet.</p> : leads.map((l) => (
        <div key={l.id} className="flex items-center justify-between p-4 border-b border-slate-50">
          <span className="text-slateink">{l.email}</span>
          <div className="flex items-center gap-3"><span className="text-xs text-slate-400">{(l.created_at || "").slice(0, 10)}</span>{l.converted && <Badge className="bg-sage/10 text-sage border-0">Converted</Badge>}</div>
        </div>
      ))}
    </div>
  );
}

function Marketing() {
  const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "G-HLNT0X0ZCJ";
  const metrics = [
    "Visitors today", "Visitors this month", "Sessions", "Traffic source",
    "Top landing pages", "Product views", "Free previews started", "Checkout starts",
    "Purchases", "Conversion rate", "Revenue", "Average order value",
    "Digital downloads", "Orders by source",
  ];
  return (
    <div className="space-y-6" data-testid="admin-marketing">
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sage/10 text-sage flex items-center justify-center"><BarChart3 className="w-5 h-5" /></div>
          <div>
            <h3 className="font-display text-xl font-bold text-slateink">Marketing & Analytics</h3>
            <p className="text-sm text-slate-500">Google Analytics 4 · <span className="font-mono text-slate-600">{GA_ID}</span></p>
          </div>
          <Badge className="ml-auto bg-sage/15 text-sage border-0" data-testid="ga-status-badge">GA4 tag installed</Badge>
        </div>
        <div className="mt-5 rounded-xl bg-amber-50 border border-amber-100 p-4">
          <p className="text-sm font-semibold text-amber-800">Google Analytics connected — reporting integration pending</p>
          <p className="text-sm text-slate-600 mt-1">
            The GA4 tracking tag is live on the storefront and collecting events (page views, product views, checkouts, purchases, refunds and studio events).
            To display live numbers inside this dashboard, connect the GA4 Data (Reporting) API. We will not show any figures until that is connected — no estimated or placeholder numbers are shown.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button asChild size="sm" variant="outline"><a href={`https://analytics.google.com/analytics/web/#/p0/realtime/overview`} target="_blank" rel="noreferrer" data-testid="open-ga-realtime">Open GA4 Realtime</a></Button>
            <Button size="sm" variant="outline" disabled data-testid="connect-reporting">Connect Reporting API (coming soon)</Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <h4 className="font-semibold text-slateink mb-1">Metrics this dashboard will show</h4>
        <p className="text-xs text-slate-400 mb-4">Available once the GA4 Reporting API is connected.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {metrics.map((m) => (
            <div key={m} className="rounded-xl border border-dashed border-slate-200 bg-subtle px-4 py-3" data-testid={`metric-${m.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="text-sm font-medium text-slate-600">{m}</p>
              <p className="text-xs text-slate-400 mt-0.5">Pending connection</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Settings() {
  const [days, setDays] = useState(30);
  useEffect(() => { api.get("/admin/settings").then((r) => setDays(r.data.photo_retention_days || 30)); }, []);
  const save = async (v) => { setDays(v); await api.patch("/admin/settings", { photo_retention_days: v }); toast.success(`Photos auto-delete after ${v} days`); };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 max-w-lg">
      <h3 className="font-semibold text-slateink">Photo data-retention policy</h3>
      <p className="text-sm text-slate-500 mt-1">Automatically delete uploaded customer photos after order completion.</p>
      <div className="flex gap-2 mt-4">
        {[30, 60, 90].map((d) => (
          <button key={d} onClick={() => save(d)} data-testid={`retention-${d}`} className={`px-5 py-2.5 rounded-xl font-medium ${days === d ? "bg-amber text-white" : "bg-subtle text-slate-600"}`}>{d} days</button>
        ))}
      </div>
    </div>
  );
}
